import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Snackbar from "@mui/material/Snackbar";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDSnackbar from "components/MDSnackbar";
import apiClient from "utils/apiClient";
import { getAuthToken } from "utils/authToken";
import { ENDPOINTS } from "utils/endpoints";
import { RED, BORDER, MUTED, GREEN, GREEN_LIGHT } from "assets/theme/brandColors";
import { MEAL_TYPE_BADGE as MEAL_TYPE_STYLE } from "utils/mealTypes";

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function ScheduleCard({
  schedule,
  recipes,
  onOpen,
  onOpenRecipe,
  onDelete,
  isSaved,
  onSave,
  strikethrough = true,
}) {
  const { title, created_at, completed_count, total_count } = schedule;
  const pct = total_count > 0 ? (completed_count / total_count) * 100 : 0;
  const allDone = total_count > 0 && completed_count === total_count;

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: "14px",
        border: "1.5px solid",
        borderColor: BORDER,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box sx={{ p: "18px 20px 14px", borderBottom: "1px solid", borderColor: BORDER }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
            mb: 1.5,
          }}
        >
          <Box>
            <Box
              sx={{
                fontSize: 15,
                fontWeight: 800,
                color: "#222",
                lineHeight: 1.3,
                fontFamily: "Roboto, sans-serif",
              }}
            >
              {title}
            </Box>
            <Box sx={{ fontSize: 11, color: MUTED, mt: "2px", fontFamily: "Roboto, sans-serif" }}>
              Created {formatDate(created_at)}
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: "6px", flexShrink: 0, alignItems: "center" }}>
            <Box
              component="button"
              onClick={onSave}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                padding: "5px 11px",
                border: "1.5px solid",
                borderRadius: "7px",
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "Roboto, sans-serif",
                whiteSpace: "nowrap",
                transition: "all 0.15s",
                ...(isSaved
                  ? { bgcolor: RED, color: "#fff", borderColor: RED }
                  : {
                      bgcolor: "#fff",
                      color: RED,
                      borderColor: RED,
                      "&:hover": { bgcolor: "#fde8ea" },
                    }),
              }}
            >
              <EventAvailableIcon sx={{ fontSize: 13 }} />
              {isSaved ? "Saved" : "Save"}
            </Box>
            <Box
              component="button"
              onClick={onOpen}
              sx={{
                padding: "6px 14px",
                bgcolor: RED,
                color: "#fff",
                border: "none",
                borderRadius: "7px",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "Roboto, sans-serif",
                whiteSpace: "nowrap",
                transition: "background 0.15s",
                "&:hover": { bgcolor: "#7d1520" },
              }}
            >
              Open
            </Box>
            <Box
              component="button"
              onClick={onDelete}
              title="Delete schedule"
              sx={{
                width: 30,
                height: 30,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1.5px solid",
                borderColor: BORDER,
                borderRadius: "7px",
                bgcolor: "#fff",
                cursor: "pointer",
                color: MUTED,
                fontSize: 15,
                transition: "all 0.15s",
                "&:hover": { borderColor: "#e57373", color: "#e57373", bgcolor: "#fff5f5" },
              }}
            >
              ✕
            </Box>
          </Box>
        </Box>

        {/* Progress bar */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box sx={{ flex: 1, height: 5, bgcolor: "#eee", borderRadius: 3, overflow: "hidden" }}>
            <Box
              sx={{
                width: `${pct}%`,
                height: "100%",
                bgcolor: allDone ? GREEN : RED,
                borderRadius: 3,
                transition: "width 0.4s",
              }}
            />
          </Box>
          <Box
            sx={{
              fontSize: 11,
              fontFamily: "Roboto, sans-serif",
              color: allDone ? GREEN : MUTED,
              fontWeight: allDone ? 700 : 400,
              whiteSpace: "nowrap",
            }}
          >
            {allDone
              ? `${total_count} / ${total_count} ✓`
              : `${completed_count} / ${total_count} done`}
          </Box>
        </Box>
      </Box>

      {/* Recipe list */}
      <Box sx={{ flex: 1 }}>
        {recipes.map((r, i) => (
          <Box
            key={r.id}
            onClick={() => onOpenRecipe && onOpenRecipe(r.id)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              px: "20px",
              py: "9px",
              borderBottom: i < recipes.length - 1 ? "1px solid #f5f5f5" : "none",
              cursor: onOpenRecipe ? "pointer" : "default",
              "&:hover": onOpenRecipe ? { bgcolor: "#fafafa" } : {},
            }}
          >
            <Box
              component="span"
              sx={{ fontSize: 13, flexShrink: 0, color: r.is_completed ? GREEN : "#ccc" }}
            >
              {r.is_completed ? "✓" : "○"}
            </Box>
            <Box
              sx={{
                fontSize: 13,
                fontFamily: "Roboto, sans-serif",
                color: r.is_completed ? MUTED : "#222",
                textDecoration: r.is_completed && strikethrough ? "line-through" : "none",
                flex: 1,
                lineHeight: 1.3,
              }}
            >
              {r.recipe_title}
            </Box>
            {MEAL_TYPE_STYLE[r.meal_type] && (
              <Box
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  px: "6px",
                  py: "2px",
                  borderRadius: "4px",
                  bgcolor: MEAL_TYPE_STYLE[r.meal_type].bg,
                  color: MEAL_TYPE_STYLE[r.meal_type].color,
                  flexShrink: 0,
                }}
              >
                {MEAL_TYPE_STYLE[r.meal_type].label}
              </Box>
            )}
            {r.is_completed && (
              <Box
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  px: "7px",
                  py: "2px",
                  borderRadius: 20,
                  bgcolor: GREEN_LIGHT,
                  color: GREEN,
                  flexShrink: 0,
                }}
              >
                Done
              </Box>
            )}
            {onOpenRecipe && (
              <ChevronRightIcon sx={{ fontSize: 16, color: "#ccc", flexShrink: 0 }} />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

ScheduleCard.propTypes = {
  schedule: PropTypes.shape({
    title: PropTypes.string,
    created_at: PropTypes.string,
    completed_count: PropTypes.number,
    total_count: PropTypes.number,
  }).isRequired,
  recipes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      recipe_title: PropTypes.string,
      is_completed: PropTypes.bool,
    })
  ).isRequired,
  onOpen: PropTypes.func.isRequired,
  onOpenRecipe: PropTypes.func,
  onDelete: PropTypes.func.isRequired,
  isSaved: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  strikethrough: PropTypes.bool,
};

function MySchedules() {
  const navigate = useNavigate();
  const [schedules, setSchedules] = useState([]);
  const [recipesByProgram, setRecipesByProgram] = useState({});
  const [savedPrograms, setSavedPrograms] = useState({});
  const [loading, setLoading] = useState(true);
  const [snack, setSnack] = useState({ open: false, content: "" });
  const [infoMsg, setInfoMsg] = useState("");
  const [confirmId, setConfirmId] = useState(null);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const isCompleted = (s) => s.total_count > 0 && s.completed_count === s.total_count;
  const activeSchedules = schedules.filter((s) => !isCompleted(s));
  const pastSchedules = schedules.filter(isCompleted);

  useEffect(() => {
    if (!getAuthToken()) {
      navigate("/authentication/sign-in", { replace: true });
      return;
    }

    (async () => {
      try {
        const res = await apiClient.get(ENDPOINTS.schedule.programs);
        const programs = Array.isArray(res.data) ? res.data : [];
        setSchedules(programs);

        const [recipeMaps, savedMaps] = await Promise.all([
          Promise.all(
            programs.map((p) =>
              apiClient
                .get(ENDPOINTS.schedule.programTitles(p.id))
                .then((r) => [p.id, Array.isArray(r.data) ? r.data : []])
                .catch(() => [p.id, []])
            )
          ),
          Promise.all(
            programs.map((p) =>
              apiClient
                .get(ENDPOINTS.collections.programSave(p.id))
                .then((r) => [p.id, r.data.saved])
                .catch(() => [p.id, false])
            )
          ),
        ]);
        setRecipesByProgram(Object.fromEntries(recipeMaps));
        setSavedPrograms(Object.fromEntries(savedMaps));
      } catch (err) {
        console.error("Failed to load schedules:", err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const handleSaveProgram = async (id) => {
    const isSaved = savedPrograms[id];
    if (!isSaved) {
      const schedule = schedules.find((s) => s.id === id);
      if (schedule && schedule.completed_count === 0) {
        setInfoMsg("Complete at least one recipe to save this schedule.");
        return;
      }
    }
    try {
      // Saving and unsaving a schedule both set a flag to a fixed value, so a
      // transport-level retry can only ever repeat the same outcome — see the
      // retry note in utils/apiClient.
      if (isSaved) {
        await apiClient.delete(ENDPOINTS.collections.programSave(id), { idempotent: true });
      } else {
        await apiClient.post(ENDPOINTS.collections.programSave(id), null, { idempotent: true });
      }
      setSavedPrograms((prev) => ({ ...prev, [id]: !isSaved }));
    } catch (err) {
      // A save that fails silently is worse than one that fails loudly: the
      // button stayed in its old state and the only trace was a console line,
      // so the user had no way to know their Cookbook hadn't changed.
      console.error("Save program failed:", err.message);
      setSnack({
        open: true,
        content:
          err.response?.data?.detail ||
          (isSaved
            ? "Couldn't remove that schedule from your Cookbook. Check your connection and try again."
            : "Couldn't save that schedule to your Cookbook. Check your connection and try again."),
      });
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiClient.delete(ENDPOINTS.schedule.deleteProgram(id));
      setSchedules((prev) => prev.filter((s) => s.id !== id));
      setRecipesByProgram((prev) => {
        const n = { ...prev };
        delete n[id];
        return n;
      });
    } catch (err) {
      console.error("Failed to delete schedule:", err.message);
      setSnack({ open: true, content: err.response?.data?.detail || "Failed to delete schedule." });
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box sx={{ pt: 3, pb: 6, px: { xs: 2, md: 3 } }}>
        {/* Page header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3.5,
            flexWrap: "wrap",
            gap: 1.5,
          }}
        >
          <Box
            sx={{ fontSize: 22, fontWeight: 800, color: "#222", fontFamily: "Roboto, sans-serif" }}
          >
            My Schedules
          </Box>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {pastSchedules.length > 0 && (
              <Box
                component="button"
                onClick={() => setArchiveOpen(true)}
                sx={{
                  padding: "9px 16px",
                  bgcolor: "transparent",
                  color: MUTED,
                  border: "1.5px solid",
                  borderColor: BORDER,
                  borderRadius: "9px",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "Roboto, sans-serif",
                  transition: "all 0.15s",
                  "&:hover": { borderColor: "#bbb", color: "#444" },
                }}
              >
                Past schedules
              </Box>
            )}
            <Box
              component="button"
              onClick={() => navigate("/schedule-builder")}
              sx={{
                padding: "9px 20px",
                bgcolor: RED,
                color: "#fff",
                border: "none",
                borderRadius: "9px",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "Roboto, sans-serif",
                transition: "background 0.15s",
                "&:hover": { bgcolor: "#7d1520" },
              }}
            >
              + New schedule
            </Box>
          </Box>
        </Box>

        {/* Content */}
        {loading ? (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              fontSize: 14,
              color: MUTED,
              fontFamily: "Roboto, sans-serif",
            }}
          >
            Loading schedules…
          </Box>
        ) : activeSchedules.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 10,
              bgcolor: "#fff",
              borderRadius: "14px",
              border: "1.5px solid",
              borderColor: BORDER,
            }}
          >
            <Box sx={{ fontSize: 36, mb: 2 }}>📋</Box>
            <Box
              sx={{
                fontSize: 16,
                fontWeight: 700,
                color: "#222",
                mb: 0.75,
                fontFamily: "Roboto, sans-serif",
              }}
            >
              {pastSchedules.length > 0 ? "All caught up!" : "No schedules yet"}
            </Box>
            <Box sx={{ fontSize: 13, color: MUTED, mb: 3, fontFamily: "Roboto, sans-serif" }}>
              {pastSchedules.length > 0
                ? "You've completed all your schedules. Build a new one to keep going."
                : "Build your first schedule to see it here."}
            </Box>
            <Box
              component="button"
              onClick={() => navigate("/schedule-builder")}
              sx={{
                padding: "10px 22px",
                bgcolor: RED,
                color: "#fff",
                border: "none",
                borderRadius: "9px",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "Roboto, sans-serif",
                "&:hover": { bgcolor: "#7d1520" },
              }}
            >
              Build a schedule
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
              gap: 2,
            }}
          >
            {activeSchedules.map((s) => (
              <ScheduleCard
                key={s.id}
                schedule={s}
                recipes={recipesByProgram[s.id] || []}
                onOpen={() => navigate(`/module/${s.id}`, { state: { from: "My Schedules" } })}
                onOpenRecipe={(recipeId) =>
                  navigate(`/module/${s.id}`, {
                    state: { from: "My Schedules", initialRecipe: String(recipeId) },
                  })
                }
                onDelete={() => setConfirmId(s.id)}
                isSaved={!!savedPrograms[s.id]}
                onSave={() => handleSaveProgram(s.id)}
              />
            ))}
          </Box>
        )}
      </Box>
      <MDSnackbar
        color="error"
        icon="warning"
        title="Error"
        dateTime=""
        content={snack.content}
        open={snack.open}
        close={() => setSnack((s) => ({ ...s, open: false }))}
      />
      <Snackbar
        open={!!infoMsg}
        autoHideDuration={3000}
        onClose={() => setInfoMsg("")}
        message={infoMsg}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
      <Dialog open={!!confirmId} onClose={() => setConfirmId(null)}>
        <DialogTitle>Delete schedule?</DialogTitle>
        <DialogContent>This cannot be undone.</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmId(null)}>Cancel</Button>
          <Button
            onClick={() => {
              handleDelete(confirmId);
              setConfirmId(null);
            }}
            sx={{ color: RED }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Past schedules archive */}
      <Dialog
        open={archiveOpen}
        onClose={() => setArchiveOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: "14px" } }}
      >
        <DialogTitle
          sx={{ fontSize: 16, fontWeight: 800, fontFamily: "Roboto, sans-serif", pb: 1 }}
        >
          Past schedules
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: 2,
              pt: 1,
            }}
          >
            {pastSchedules.map((s) => (
              <ScheduleCard
                key={s.id}
                schedule={s}
                recipes={recipesByProgram[s.id] || []}
                onOpen={() => {
                  setArchiveOpen(false);
                  navigate(`/module/${s.id}`, { state: { from: "My Schedules" } });
                }}
                onDelete={() => {
                  setArchiveOpen(false);
                  setConfirmId(s.id);
                }}
                isSaved={!!savedPrograms[s.id]}
                onSave={() => handleSaveProgram(s.id)}
                strikethrough={false}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setArchiveOpen(false)} sx={{ color: MUTED, fontSize: 13 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default MySchedules;
